import { Button } from "@pepperstone/pepper-ui-kit";
import { useMemo, useState } from "react";

import { Datepicker } from "../../Datepicker";
import { DropdownOption, DropDownV2 } from "../../DropDownV2";
import { useIsMobile } from "../../hooks";
import { SearchInput } from "../../SearchInput";
import { useSearchPlaceHolder } from "../hooks/useSearchPlaceHolder";
import { useLeadsContext } from "../providers/LeadsProvider";
import { LeadTableFilterSubProps } from "./types";

export const MobileFilters = ({
  handleDateRangePicker,
  handleExportButton,
  handleSearchInput,
  handleLeadStatusDropdown,
  canViewPII,
  translations: { translatedContent, language },
  isBackoffice,
}: LeadTableFilterSubProps) => {
  const isMobile = useIsMobile();
  const {
    state: {
      searchQuery,
      dateRange,
      selected: { leadStatuses: selectedLeadStatuses },
    },
    resources: {
      table: { data: tableData },
    },
    options: { leadStatuses },
  } = useLeadsContext();

  const searchPlaceHolder = useSearchPlaceHolder({
    canViewPII,
    redactedFields: tableData?.redactedFields,
    translatedContent,
  });

  const [search, setSearch] = useState(searchQuery);

  const selectedRebateAccounts = useMemo(() => {
    return ((selectedLeadStatuses as string[]) ?? []).map((id) => ({
      label: leadStatuses.find((option) => option.value === id)?.label || id,
      value: id,
    }));
  }, [leadStatuses, selectedLeadStatuses]);

  const handleDropdownChange = (options: DropdownOption[]) => {
    handleLeadStatusDropdown?.(options);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    handleSearchInput?.(value);
  };

  return (
    <div className="mt-4 flex flex-col gap-4">
      <div data-testid="mobile-date-picker">
        <Datepicker
          inputId="mobileDateRangeFilter"
          label={translatedContent.datePickerLabel}
          value={{
            startDate: dateRange.from,
            endDate: dateRange.to,
          }}
          onChange={handleDateRangePicker}
          configs={{
            shortcuts: {
              today: translatedContent.today,
              yesterday: translatedContent.yesterday,
              past: (period) =>
                translatedContent.past ? translatedContent.past(period) : "",
              currentMonth: translatedContent.currentMonth,
              pastMonth: translatedContent.pastMonth,
            },
            footer: {
              cancel: translatedContent.cancel,
              apply: translatedContent.apply,
            },
          }}
          placeholder={translatedContent.datePickerPlaceholder}
          i18n={language}
          isMobile={isMobile}
        />
      </div>
      <SearchInput
        label={translatedContent.searchLabel}
        value={search}
        onChange={handleSearchChange}
        placeholder={searchPlaceHolder}
      />
      <div className="w-full">
        <DropDownV2
          id="mobileStatusDropdown"
          options={leadStatuses}
          selected={selectedRebateAccounts}
          label={translatedContent.statusesFilterLabel}
          isMobile={isMobile}
          dropDownLabels={translatedContent.dropDownLabels}
          handleApply={handleDropdownChange}
        />
      </div>
      {!isBackoffice ? (
        <Button
          as="button"
          type="button"
          size="ps-btn-md"
          variant="ps-btn-secondary"
          onClick={handleExportButton}
        >
          {translatedContent.exportCSVText}
        </Button>
      ) : null}
    </div>
  );
};
